import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Radio, Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import Logo from "./Logo";

const NAV = [
  { label: "Hakkımızda", to: "/hakkimizda" },
  { label: "Programlarımız", to: "/programlar" },
  { label: "Program Videoları", to: "/videolar" },
  { label: "Canlı Yayın", to: "/canli-yayin" },
  { label: "Bize Ulaşın", to: "/iletisim" },
];

export default function Header() {
  const headerRef = useRef(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        el.classList.toggle("is-scrolled", window.scrollY > 20);
        ticking = false;
      });
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/videolar?q=${encodeURIComponent(query.trim())}`);
      setSearchOpen(false);
      setQuery("");
    }
  };

  return (
    <header ref={headerRef} className="site-header fixed top-0 left-0 right-0 z-50">
      <div className="tv-container flex items-center justify-between h-[74px]">
        <Logo size="md" />

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className={`relative text-sm font-medium transition-colors duration-300 group ${
                location.pathname === n.to ? "text-white" : "text-[var(--tv-muted)] hover:text-white"
              }`}
            >
              {n.label}
              <span
                className={`absolute -bottom-1.5 left-0 h-0.5 rounded-full bg-gradient-to-r from-[#7c3aed] to-[#a855f7] transition-all duration-300 ${
                  location.pathname === n.to ? "w-full" : "w-0 group-hover:w-full"
                }`}
              />
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setSearchOpen((s) => !s)}
            className="w-10 h-10 rounded-full border border-[var(--tv-border)] flex items-center justify-center text-[var(--tv-muted)] hover:text-white hover:border-[rgba(168,85,247,0.5)] transition-colors"
            aria-label="Ara"
          >
            {searchOpen ? <X size={18} /> : <Search size={18} />}
          </button>
          <Link to="/canli-yayin" className="tv-btn-primary hidden sm:inline-flex !px-5 !py-2.5 text-sm">
            <Radio size={16} /> Canlı İzle
          </Link>
          <button
            onClick={() => setMobileOpen((m) => !m)}
            className="lg:hidden w-10 h-10 rounded-full border border-[var(--tv-border)] flex items-center justify-center text-white"
            aria-label="Menü"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {searchOpen && (
          <motion.form
            onSubmit={submitSearch}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="tv-container overflow-hidden"
          >
            <div className="pb-4 flex items-center gap-2 border-b border-[var(--tv-border)]">
              <Search size={18} className="text-[var(--tv-muted)]" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Video, program ara..."
                className="flex-1 bg-transparent outline-none text-white placeholder:text-[var(--tv-muted)] py-1"
              />
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-[#06060d]/98 border-t border-[var(--tv-border)]"
          >
            <div className="tv-container py-5 flex flex-col gap-1">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  className="py-3 px-2 rounded-lg text-[var(--tv-text)] hover:bg-[rgba(139,92,246,0.1)] transition-colors"
                >
                  {n.label}
                </Link>
              ))}
              <Link to="/canli-yayin" className="tv-btn-primary mt-3 w-full">
                <Radio size={16} /> Canlı İzle
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
